import { css } from "@emotion/css";
import { ActionButton, ActionButtonType, Dialog, DialogActions, DialogContent, DialogTitle, PopoverBasicHelp, RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import { ParseHelper, DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { Permission, type Repository, type SpaceDefaultRunbookRetentionPolicyResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { useState } from "react";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { RedirectAs404 } from "~/components/NotFound/NotFound";
import { PageContent } from "~/components/PageContent/PageContent";
import { FormSection, Text, minimum } from "~/components/form";
import { Form, useForm } from "~/components/form/Form/Form";
//use the usePageRegistration hook
export interface RunbookRetentionPoliciesPageLoaderData {
    defaultRetentionPolicy: SpaceDefaultRunbookRetentionPolicyResource;
}
interface RunbookRetentionPoliciesPageProps {
    spaceId: string;
    loaderData: RunbookRetentionPoliciesPageLoaderData;
}
export async function runbookRetentionPoliciesPageLoader(repository: Repository): Promise<RunbookRetentionPoliciesPageLoaderData> {
    if (!isFeatureToggleEnabled("centralized-retention-runbook")) {
        return {
            defaultRetentionPolicy: {
                RetentionType: "RunbookRetention",
                Strategy: "Forever",
                SpaceId: "",
                Id: "",
                Name: "",
            },
        };
    }
    const defaultRunbookRetentionPolicyConfiguration = repository.SpaceDefaultRetentionPolicies.getDefaultByType("RunbookRetention");
    return {
        // Repository will always return a SpaceDefaultRunbookRetentionPolicyResource, so we can safely cast it
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        defaultRetentionPolicy: (await defaultRunbookRetentionPolicyConfiguration) as SpaceDefaultRunbookRetentionPolicyResource,
    };
}
export default function RunbookRetentionPoliciesPage(props: RunbookRetentionPoliciesPageProps) {
    if (!isFeatureToggleEnabled("centralized-retention-runbook")) {
        return <RedirectAs404 />;
    }
    return <RunbookRetentionPoliciesPageInternal {...props}/>;
}
function RunbookRetentionPoliciesPageInternal(props: RunbookRetentionPoliciesPageProps) {
    const { loaderData } = props;
    const defaultRunbookRetentionPageTitle = "Runbook Retention";
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const { model, setModel, createSaveAction, formProps, isDisabled } = useForm({
        formName: "Edit Default Runbook Retention Policy",
        initialModel: loaderData.defaultRetentionPolicy,
        submitPermission: { permission: [Permission.RetentionAdminister] },
        onSubmit: async (repository, model) => 
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        (await repository.SpaceDefaultRetentionPolicies.updateDefault(model)) as SpaceDefaultRunbookRetentionPolicyResource,
        afterSubmit: async () => setSaveDialogOpen(false),
        updateFormModelAfterSubmit: (model) => model,
    });
    return (<PageContent header={{
            title: defaultRunbookRetentionPageTitle,
            primaryAction: {
                type: "button",
                label: isDisabled.value && isDisabled.reason === "no permissions" ? isDisabled.missingPermissionsLabel : "Save",
                disabled: isDisabled.value,
                onClick: () => setSaveDialogOpen(true),
            },
            showBreadcrumbBackIcon: true,
            breadcrumbs: [{ label: "Retention Policies", pageUrl: links.retentionPoliciesPage.generateUrl({ spaceId: props.spaceId }) }],
        }}>
            <Dialog open={saveDialogOpen} dialogWidth="480px">
                <DialogTitle>Update retention policy?</DialogTitle>
                <DialogContent>This policy will apply to all runbooks using the “Default retention policy”</DialogContent>
                <DialogActions>
                    <ActionButton label="Cancel" onClick={() => setSaveDialogOpen(false)} type={ActionButtonType.Secondary}/>
                    <ActionButton {...createSaveAction({ saveButtonLabel: "Update", saveButtonBusyLabel: "Updating..." })} type={ActionButtonType.Primary}/>
                </DialogActions>
            </Dialog>
            <BorderedContent>
                <Form {...formProps}>
                    <FormSection title={<span className={styles.formSectionTitleContainer}>
                                Default Retention Policy
                                <PopoverBasicHelp description="This retention policy will be applied to all runbooks that use the “Default retention policy”" placement="right-start"/>
                            </span>} help="This retention policy dictates how long Runbook runs are kept for.">
                        <RadioButtonGroup title="How many Runbook runs should we keep per environment?" value={model.Strategy} onChange={(newStrategy) => {
            setModel((prevModel) => ({ ...prevModel, Strategy: newStrategy, ...(newStrategy === "Count" ? { QuantityToKeep: prevModel.QuantityToKeep ?? 100, Unit: prevModel.Unit ?? "Items" } : {}) }));
        }}>
                            <RadioButton label="Keep all" value="Forever"/>
                            <RadioButton label="Keep a limited number" value="Count"/>
                            {model.Strategy === "Count" && (<div className={styles.countStrategyConfigContainer}>
                                    <span>
                                        <Text type="number" className={styles.quantityTextbox} min={1} value={model.QuantityToKeep?.toString() ?? ""} label={model.Unit === "Items" ? "Retained Runs" : "Days to Keep"} onChange={(newQuantityToKeep) => {
                setModel((prev) => {
                    return { ...prev, QuantityToKeep: ParseHelper.safeParseInt(newQuantityToKeep, 0) };
                });
            }} validate={minimum("Please provide a value greater than zero.", 1)}/>
                                    </span>
                                    <span>
                                        <DeprecatedSelect value={model.Unit ?? ""} label="Retention Unit" fieldName="RetentionUnit" onChange={(newUnit) => {
                setModel((prev) => ({ ...prev, Unit: parseRetentionPolicyUnit(newUnit ?? "") }));
            }} items={[
                { value: "Days", text: "Days" },
                { value: "Items", text: "Runs" },
            ]}/>
                                    </span>
                                </div>)}
                        </RadioButtonGroup>
                    </FormSection>
                </Form>
            </BorderedContent>
        </PageContent>);
}
const styles = {
    formSectionTitleContainer: css({
        display: "flex",
        flexWrap: "nowrap",
        flexDirection: "row",
        alignItems: "center",
        gap: space[4],
    }),
    countStrategyConfigContainer: css({
        paddingTop: space[16],
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        gap: space[8],
    }),
    quantityTextbox: css({
        width: "180px",
    }),
};
function parseRetentionPolicyUnit(unit: string): "Days" | "Items" | undefined {
    switch (unit) {
        case "Days":
            return "Days";
        case "Items":
            return "Items";
        default:
            return undefined;
    }
}
